import { assertNever } from "@impero-com/common/utils/obj";
import { useQuery } from "@tanstack/react-query";

import { PageState } from "api/generated/model";
import { getShareableLinksId } from "api/generated/shareable-links/shareable-links";

import { UUID_REGEX } from "../consts";
import { getDefaultPageState } from "../defaults";
import { PageKey } from "../pageKey";
import { encodeKey, pageStateStorage } from "../storage";
import { getVersionForPageState } from "../utils";

import { usePageStateMigrationMutation } from "./UsePageStateMigrationMutation";

export const PAGE_STATE_QUERY_KEY = "pageState";

export function usePageStateQuery(
    pageKey: PageKey,
    shareableLinkId: string | null,
    callback?: (pageState: PageState) => void
) {
    const { mutateAsync: migratePageState } = usePageStateMigrationMutation();
    return useQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: [PAGE_STATE_QUERY_KEY, encodeKey(pageKey), shareableLinkId],
        queryFn: async () => {
            if (!shareableLinkId) {
                const pageState = loadPageStateFromStorage(pageKey);
                const checkedPageState = await checkPageStateVersion(
                    pageState,
                    pageKey,
                    migratePageState
                );
                callback?.(checkedPageState);
                return checkedPageState;
            }
            if (!UUID_REGEX.test(shareableLinkId)) {
                throw new Error("Invalid shareable link ID");
            }
            const response = await getShareableLinksId(shareableLinkId, {
                validateStatus: (_status) => true,
            });
            const checkedPageState = await checkPageStateVersion(
                response.data.pageState,
                pageKey,
                migratePageState
            );
            callback?.(checkedPageState);
            return checkedPageState;
        },
    });
}

function loadPageStateFromStorage(pageKey: PageKey): PageState {
    const defaultPageState: PageState = getDefaultPageState(pageKey.type);
    return pageStateStorage.getOr(pageKey, defaultPageState);
}

export async function checkPageStateVersion(
    pageState: PageState,
    pageKey: PageKey,
    migratePageState: ReturnType<
        typeof usePageStateMigrationMutation
    >["mutateAsync"]
): Promise<PageState> {
    return new Promise((resolve, _reject) => {
        const currentVersion = getVersionForPageState(pageState.type);
        let version = currentVersion;

        switch (pageState.type) {
            case "ControlsWeekly":
            case "ControlsAll":
            case "ControlsHalfYearly":
            case "ControlsManual":
            case "ControlsMonthly":
            case "ControlsQuarterly":
            case "ControlsTwoMonths":
            case "ControlsYearly":
            case "Activities":
            case "ControlPrograms":
            case "ControlProgram":
            case "DataSheetReport":
            case "Users":
            case "UserGroups":
            case "Tags":
            case "ReportsAll":
            case "ReportsStatus":
            case "ReportsClassification":
            case "ReportsDatasheet":
            case "ReportsWorkload":
            case "Dashboard":
                version = pageState.version ?? 0;
                break;
            case "RiskDirectory":
            case "RiskMap":
                version = pageState.filtersAndColumns.version ?? 0;
                break;
            default:
                assertNever(pageState);
        }

        if (version !== currentVersion) {
            migratePageState({
                data: {
                    version: version ?? 0,
                    page_state: pageState.type,
                    data: pageState,
                },
            })
                .then((newPageState) => {
                    const migratedPageState = newPageState.data;
                    pageStateStorage.set(pageKey, migratedPageState); // Save the migrated data to local storage
                    resolve(migratedPageState);
                })
                .catch(() => {
                    resolve(pageState);
                });
        } else {
            resolve(pageState);
        }
    });
}
